import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from 'context/user/User';
import stationOrdersApi from 'api/requests/stationOrders';
import orderStationsApi from 'api/requests/orderStations';
import pusherService from 'services/pusher';
import styled, { keyframes } from 'styled-components';
import OrderCard from './OrderCard';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import paths from 'routes/paths';

const COLUMNS = [
    { key: 'pending',   label: 'Pendente',   color: colors.occasionalOrange },
    { key: 'preparing', label: 'Preparando', color: colors.occasionalBlue || '#003393' },
    { key: 'ready',     label: 'Pronto',     color: colors.occasionalGreen },
    { key: 'delivered', label: 'Entregue',   color: colors.nightLight },
];

const POLL_INTERVAL = 30000;

export default function KitchenPage() {
    const [orders, setOrders] = useState([]);
    const [stations, setStations] = useState([]);
    const [stationsLoaded, setStationsLoaded] = useState(false);
    const [activeStation, setActiveStation] = useState(null); // null = all
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [mobileTab, setMobileTab] = useState('pending');
    const [advancingIds, setAdvancingIds] = useState(new Set());
    const [isLive, setIsLive] = useState(false);
    const intervalRef = useRef(null);
    const inflightRef = useRef(new Set());
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;
    const establishmentId = user?.establishments?.[0]?.id;

    useEffect(() => {
        if (!slug) return;
        orderStationsApi.list(slug)
            .then(({ data }) => {
                setStations(data.data || data || []);
                setStationsLoaded(true);
            })
            .catch(() => {});
    }, [slug]);

    useEffect(() => {
        if (!slug) return;
        let cancelled = false;

        function fetchOrders() {
            stationOrdersApi.list(slug)
                .then(({ data }) => {
                    if (cancelled) return;
                    if (inflightRef.current.size === 0) {
                        setOrders(data.data || []);
                        setLastUpdated(new Date());
                    }
                })
                .catch(() => {});
        }

        fetchOrders();
        intervalRef.current = setInterval(fetchOrders, POLL_INTERVAL);
        return () => {
            cancelled = true;
            clearInterval(intervalRef.current);
        };
    }, [slug]);

    const handleOrderCreated = useCallback((data) => {
        setOrders(prev => {
            if (prev.some(o => o.id === data.id)) return prev;
            return [...prev, data];
        });
        setLastUpdated(new Date());
    }, []);

    const handleStatusUpdated = useCallback((data) => {
        if (inflightRef.current.has(data.id)) return;

        setOrders(prev => prev.map(o =>
            o.id === data.id ? { ...o, status: data.status } : o
        ));
        setLastUpdated(new Date());
    }, []);

    useEffect(() => {
        if (!establishmentId) return;

        const channelName = `establishments.${establishmentId}`;
        pusherService.subscribe(channelName, 'station_order.created', handleOrderCreated);
        pusherService.subscribe(channelName, 'station_order.status_updated', handleStatusUpdated);

        const channel = pusherService.channels[channelName];
        if (channel) {
            channel.bind('pusher:subscription_succeeded', () => setIsLive(true));
            channel.bind('pusher:subscription_error', () => setIsLive(false));
        }

        const connHandler = (state) => {
            setIsLive(state.current === 'connected');
        };
        pusherService.pusher.connection.bind('state_change', connHandler);

        if (pusherService.pusher.connection.state === 'connected' && channel) {
            setIsLive(true);
        }

        return () => {
            pusherService.pusher.connection.unbind('state_change', connHandler);
            pusherService.unsubscribe(channelName, 'station_order.created', handleOrderCreated);
            pusherService.unsubscribe(channelName, 'station_order.status_updated', handleStatusUpdated);
            setIsLive(false);
        };
    }, [establishmentId, handleOrderCreated, handleStatusUpdated]);

    async function handleAdvance(orderId, nextStatus) {
        if (!nextStatus) return;
        const order = orders.find(o => o.id === orderId);
        if (!order) return;
        if (advancingIds.has(orderId)) return;

        // Optimistic update
        setAdvancingIds(prev => new Set([...prev, orderId]));
        setOrders(prev => prev.map(o =>
            o.id === orderId ? { ...o, status: nextStatus } : o
        ));

        inflightRef.current.add(orderId);
        try {
            await stationOrdersApi.updateStatus(slug, orderId, nextStatus);
        } catch {
            // Revert on error
            setOrders(prev => prev.map(o =>
                o.id === orderId ? { ...o, status: order.status } : o
            ));
        } finally {
            inflightRef.current.delete(orderId);
            setAdvancingIds(prev => { const s = new Set(prev); s.delete(orderId); return s; });
        }
    }

    function getVisibleOrders() {
        if (activeStation === null) return orders;
        return orders.filter(o => o.station_id === activeStation);
    }

    function getColumnOrders(status) {
        return getVisibleOrders().filter(o => o.status === status);
    }

    const visibleOrders = getVisibleOrders();
    const minutesAgo = Math.floor((Date.now() - lastUpdated.getTime()) / 60000);

    return (
        <Page>
            <PageHeader>
                <HeaderLeft>
                    <PageTitle>Pedidos</PageTitle>
                    <UpdateBadge $live={isLive}>
                        {isLive ? 'Ao vivo' : minutesAgo === 0 ? 'Atualizado agora' : `Atualizado há ${minutesAgo}min`}
                    </UpdateBadge>
                </HeaderLeft>
                <TotalBadge>{visibleOrders.length} pedido{visibleOrders.length !== 1 ? 's' : ''}</TotalBadge>
            </PageHeader>

            {/* Only products with a station reach this board */}
            {stationsLoaded && stations.length === 0 && (
                <NoStationsNotice>
                    Só produtos com estação aparecem aqui, e nenhuma estação foi cadastrada.
                    Crie as estações em <Link to={paths.dashboard.establishment.admin}>Administração</Link> e
                    escolha a estação de cada produto no <Link to={paths.dashboard.establishment.menu}>Cardápio</Link>.
                </NoStationsNotice>
            )}

            {/* Station tabs — only shown when there are stations */}
            {stations.length > 0 && (
                <StationTabBar>
                    <StationTab
                        $active={activeStation === null}
                        onClick={() => setActiveStation(null)}
                    >
                        Todos
                        <StationTabCount $active={activeStation === null}>
                            {orders.length}
                        </StationTabCount>
                    </StationTab>
                    {stations.map(s => {
                        const count = orders.filter(o => o.station_id === s.id).length;
                        return (
                            <StationTab
                                key={s.id}
                                $active={activeStation === s.id}
                                onClick={() => setActiveStation(s.id)}
                            >
                                {s.name}
                                <StationTabCount $active={activeStation === s.id}>
                                    {count}
                                </StationTabCount>
                            </StationTab>
                        );
                    })}
                </StationTabBar>
            )}

            {/* Mobile status tab bar */}
            <MobileTabBar>
                {COLUMNS.map(col => (
                    <MobileTab
                        key={col.key}
                        $active={mobileTab === col.key}
                        $color={col.color}
                        onClick={() => setMobileTab(col.key)}
                    >
                        {col.label}
                        <MobileTabCount $active={mobileTab === col.key} $color={col.color}>
                            {getColumnOrders(col.key).length}
                        </MobileTabCount>
                    </MobileTab>
                ))}
            </MobileTabBar>

            {/* Mobile Content */}
            <MobileContent>
                {getColumnOrders(mobileTab).length === 0
                    ? <EmptyCol>Nenhum pedido {COLUMNS.find(c => c.key === mobileTab)?.label?.toLowerCase()}</EmptyCol>
                    : getColumnOrders(mobileTab).map(order => (
                        <OrderCard key={order.id} order={order} onAdvance={handleAdvance}
                            color={COLUMNS.find(c => c.key === order.status)?.color}
                            isAdvancing={advancingIds.has(order.id)} />
                    ))
                }
            </MobileContent>

            {/* Desktop Kanban */}
            <KanbanBoard>
                {COLUMNS.map(col => (
                    <KanbanColumn key={col.key}>
                        <ColumnHeader $color={col.color}>
                            <ColumnTitle>{col.label}</ColumnTitle>
                            <ColumnCount $color={col.color}>{getColumnOrders(col.key).length}</ColumnCount>
                        </ColumnHeader>
                        <ColumnCards>
                            {getColumnOrders(col.key).length === 0
                                ? <EmptyCol>Sem pedidos</EmptyCol>
                                : getColumnOrders(col.key).map(order => (
                                    <OrderCard key={order.id} order={order} onAdvance={handleAdvance} color={col.color}
                                        isAdvancing={advancingIds.has(order.id)} />
                                ))
                            }
                        </ColumnCards>
                    </KanbanColumn>
                ))}
            </KanbanBoard>
        </Page>
    );
}

/* Styled Components */

const Page = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #f0f0f3;
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #fff;
    border-bottom: 1px solid ${colors.neutralDark};
    flex-shrink: 0;
`;

const HeaderLeft = styled.div`display: flex; align-items: center; gap: 10px;`;
const PageTitle = styled.h2`font-size: 18px; font-weight: 600; color: ${colors.nightDark}; margin: 0;`;
const UpdateBadge = styled.span`
    font-size: 12px;
    padding: 3px 8px;
    border-radius: 8px;
    color: ${({ $live }) => $live ? '#fff' : colors.nightLight};
    background: ${({ $live }) => $live ? colors.occasionalGreen : colors.neutralDark};
    transition: all 0.3s ease;
`;
const TotalBadge = styled.span`font-size: 13px; font-weight: 600; color: ${colors.night};`;

const NoStationsNotice = styled.div`
    padding: 10px 16px;
    font-size: 13px;
    line-height: 1.45;
    color: ${colors.nightDark};
    background: ${colors.occasionalOrange}1a;
    border-bottom: 1px solid ${colors.occasionalOrange}40;
    flex-shrink: 0;
    a { color: inherit; font-weight: 600; text-decoration: underline; }
`;

/* Station Tabs */
const StationTabBar = styled.div`
    display: flex;
    background: #fff;
    border-bottom: 1px solid ${colors.neutralDark};
    flex-shrink: 0;
    overflow-x: auto;
    padding: 0 8px;
    gap: 2px;
    &::-webkit-scrollbar { display: none; }
`;

const StationTab = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: ${({ $active }) => $active ? '700' : '500'};
    color: ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : colors.nightLight};
    background: none;
    border: none;
    border-bottom: 2.5px solid ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : 'transparent'};
    margin-bottom: -1px;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.15s;
    &:hover { color: ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : colors.night}; }
`;

const StationTabCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    padding: 0 5px;
    font-size: 11px;
    font-weight: 600;
    background: ${({ $active }) => $active ? `${panelAccentAlpha(colors.occasionalPurple, '18')}` : colors.neutralDark};
    color: ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : colors.nightLight};
    transition: all 0.15s;
`;

/* Mobile */
const MobileTabBar = styled.div`
    display: flex;
    background: #fff;
    border-bottom: 2px solid ${colors.neutralDark};
    flex-shrink: 0;
    @media screen and (min-width: ${breakpoints.lg}) { display: none; }
`;

const MobileTab = styled.button`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 11px 8px;
    font-size: 13px;
    font-weight: ${({ $active }) => $active ? '700' : '400'};
    color: ${({ $active, $color }) => $active ? $color : colors.nightLight};
    background: none;
    border: none;
    border-bottom: 2px solid ${({ $active, $color }) => $active ? $color : 'transparent'};
    margin-bottom: -2px;
    cursor: pointer;
    transition: color 0.15s;
`;

const MobileTabCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    padding: 0 5px;
    font-size: 11px;
    font-weight: 600;
    background: ${({ $active, $color }) => $active ? $color : colors.neutralDark};
    color: ${({ $active }) => $active ? '#fff' : colors.nightLight};
    transition: all 0.15s;
`;

const MobileContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    @media screen and (min-width: ${breakpoints.lg}) { display: none; }
`;

/* Desktop Kanban */
const KanbanBoard = styled.div`
    display: none;
    @media screen and (min-width: ${breakpoints.lg}) {
        display: flex;
        flex: 1;
        overflow: hidden;
    }
`;

const KanbanColumn = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    border-right: 1px solid ${colors.neutralDark};
    &:last-child { border-right: none; }
`;

const ColumnHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #fff;
    border-bottom: 3px solid ${({ $color }) => $color || colors.neutralDark};
    flex-shrink: 0;
`;

const ColumnTitle = styled.span`font-weight: 600; font-size: 15px; color: ${colors.nightDark};`;
const ColumnCount = styled.span`
    background: ${({ $color }) => $color || colors.neutralDark}25;
    color: ${({ $color }) => $color || colors.nightDark};
    border-radius: 10px;
    padding: 2px 10px;
    font-size: 13px;
    font-weight: 600;
`;

const ColumnCards = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: #f0f0f3;
`;

const EmptyCol = styled.div`
    text-align: center;
    padding: 32px 0;
    color: ${colors.nightLight};
    font-size: 14px;
`;
